import apiClient from "../api/axios";
import alertService from "../components/alert/alertService";
import { handleApiError } from "../api/handleApiError";
import type { AxiosError, AxiosResponse } from "axios";
import type {
  GetActivities,
  ActivityAttachmentDetails,
} from "../models/Activities";
import type {
  ResponseSummaryStatu,
  SummaryStatu,
} from "../models/MonthlyStatus";
import type { ActivityAttachmentDTO } from "../models/ActivityAttachment";
import type { DeleteActivityResponse } from "../models/dto/response/DeleteActivityResponse";
import type { Activity, ActivitySearchFilter } from "../models/Activities";
import type { ApiRequestConfig } from "../models/api-responses.types";

const BASE = {
  v1: "/v1/activities",
  v2: "/v2/activities",
  v3: "/v3/activities",
} as const;

// ---------- helpers ----------
async function withLoading<T>(task: () => Promise<T>): Promise<T> {
  alertService.showLoading();
  try {
    return await task();
  } finally {
    alertService.closeLoading();
  }
}

const ATTACHMENT_REFUSAL_CODES = [
  "FORBIDDEN_ACTION_ERROR",
  "PLAN_EXPIRED",
  "INVALID_FILE_EXTENSION",
  "EVIDENCE_CONTENT_MISMATCH",
];

const handleError = (error: unknown) =>
  handleApiError(
    error,
    {
      FORBIDDEN_ACCESS_ERROR: "Forbidden-Access-Error",
      ACTIVITY_UPSERT_PROCESS_FAILED: "Forbidden-Access-Error",
    },
    "Forbidden-Access-Error",
  );

// ---------- service ----------
const activitiesService = {
  create: (
    formData: Record<string, unknown>,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.post(`${BASE.v3}`, formData);
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  update: (
    activityRefId: string,
    formData: Record<string, unknown>,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.post(`${BASE.v2}`, {
        ...formData,
        activityRefId,
      });
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  updateV3: (
    activityRefId: string,
    formData: Record<string, unknown>,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.put(`${BASE.v3}/${activityRefId}`, {
        ...formData,
      });
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  getByYear: (year: number): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.get(`${BASE.v1}/${year}`);
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  updateAmount: (
    activitiesRef: string,
    amount: number,
    year: number,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.patch(`${BASE.v1}/${activitiesRef}`, {
        amount,
        year,
      });
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  softDelete: (
    activitiesRef: string,
    year: number,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.patch(`${BASE.v1}/${activitiesRef}`, {
        status: "delete",
        year,
      });
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  updateAttachment: (
    activitiesRef: string,
    fileBase64: string | "", // "" = remove
    year: number,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.patch(`${BASE.v1}/${activitiesRef}`, {
        attachment: fileBase64,
        year,
      });
      return (res.data as { results: GetActivities }).results;
    }).catch((error: AxiosError<{ code?: string }>) => {
      // plan and file-type refusals get their own message; every other failure stays "upload incomplete"
      const code = error.response?.data?.code;
      if (code && ATTACHMENT_REFUSAL_CODES.includes(code)) {
        return handleApiError(error);
      }
      alertService.error("upload-incomplete");
      return undefined;
    }),

  updateStatus: (
    activitiesRef: string,
    status: string,
    year: number,
  ): Promise<GetActivities | undefined> =>
    withLoading(async () => {
      const res = await apiClient.patch(`${BASE.v1}/${activitiesRef}`, {
        status,
        year,
      });
      return (res.data as { results: GetActivities }).results;
    }).catch(handleError),

  search: (param?: ActivitySearchFilter): Promise<GetActivities | undefined> =>
    apiClient
      .post(`${BASE.v1}/search`, param)
      .then((res) => (res.data as { results: GetActivities }).results)
      .catch(handleError),

  // No alert, no loading popup: either would replace the open save dialog and lose its Save.
  searchTemplateMonths: async (
    param: ActivitySearchFilter,
  ): Promise<Activity[] | null> => {
    const config: ApiRequestConfig = { _suppressGlobalAlert: true };
    try {
      const res = await apiClient.post(`${BASE.v1}/search`, param, config);
      return (res.data as { results: GetActivities }).results.activities;
    } catch {
      return null;
    }
  },

  getMonthlyStatus: (year: string): Promise<SummaryStatu[] | undefined> =>
    apiClient
      .get(`${BASE.v1}/status/${year}`)
      .then(
        (res) =>
          (res.data as { results: ResponseSummaryStatu }).results.summaryStatus,
      )
      .catch(handleError),

  getAttachmentMeta: (refId: string) =>
    withLoading(async () => {
      const res = await apiClient.get(`/v1/activities/attachment/${refId}`);
      return (res.data as ActivityAttachmentDTO).results;
    }).catch(handleError),

  multipleSoftDelete: (
    activityRefs: string[],
    year: number,
  ): Promise<DeleteActivityResponse | undefined> =>
    withLoading(async () => {
      const res = await apiClient.patch(`/v1/activities/delete`, {
        activityRefs,
        year,
      });
      return res.data as DeleteActivityResponse;
    }).catch(handleError),

  getAttachment: (
    attachmentRef: string,
  ): Promise<ActivityAttachmentDetails | undefined> =>
    apiClient
      .get(`${BASE.v2}/attachment/${attachmentRef}`)
      .then(
        (res: AxiosResponse) =>
          (res.data as { results: ActivityAttachmentDetails }).results,
      )
      .catch(handleError),
};

export default activitiesService;
